import { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import { AppConstants } from '../../constants/AppConstants';
import { Switch, ToggleButton } from '../shared/Fields/Switch';
import { DualingListPick } from '../shared/DualingList/DualingList';
import FeatureDetailModal from '../Features/FeatureModal';
import { isEmpty, find, isEqual, cloneDeep, uniqBy } from 'lodash';
import { useParams } from 'react-router-dom';
import { XIcon } from '@heroicons/react/solid';

interface PillsProps {
  name: string;
  onClick: any;
  isSelected: boolean;
  onRemove: Function;
  editable: boolean;
}

const Pills = ({ name, onClick, isSelected, onRemove, editable }: PillsProps) => {
  const { t } = useTranslation();
  return (
    <div className={`m-2 ml-0 flex cursor-pointer flex-row`} onClick={onClick}>
      <div
        className={`flex items-center rounded-[60px] border-[1px] ${
          !isSelected ? 'border-gray-400' : '!border-[2px] border-primary'
        } bg-secondary px-2 py-1`}
      >
        <p className="flex-grow text-base font-normal capitalize leading-6">{name}</p>
        {editable ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="ml-1"
          >
            <XIcon className="h-5 w-5 text-gray-600" />
          </button>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

interface Action {
  id: string;
  actionName: string;
}

interface Operation {
  id: string;
  name: string;
}
interface Feature {
  id: string;
  name: string;
  description?: string;
  actions: Action[];
  allActions: boolean;
  operations: Operation[];
}

interface FeatureOperations {
  [featureId: string]: {
    initialData: Operation[];
    outputData: Action[];
    allActions: boolean;
  };
}

interface AssignFeaturesProps {
  isEdit: boolean;
  featureListData: Feature[];
  setFeatureListData: React.Dispatch<React.SetStateAction<Feature[]>>;
  featurePayload: Feature[];
  setFeaturePayload: React.Dispatch<React.SetStateAction<Feature[]>>;
  featureOperations: FeatureOperations;
  setFeatureOperations: React.Dispatch<React.SetStateAction<FeatureOperations>>;
}

const AssignFeatures = ({
  isEdit,
  featureListData,
  setFeatureListData,
  featurePayload,
  setFeaturePayload,
  featureOperations,
  setFeatureOperations
}: AssignFeaturesProps) => {
  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<any>(null);
  const [createFeatureModal, setCreateFeatureModal] = useState(false);
  const [featureListMap, setFeatureListMap] = useState<any>({});
  const [featureListDataClone, setFeatureListDataClone] = useState<any>([]);
  const [initFlag, setInitFlag] = useState<boolean>(false);
  const [renderKey, setRenderKey] = useState<number>(Math.random());

  const featureOperationsRef = useRef(featureOperations);

  const dispatch = useAppDispatch();
  const params = useParams();
  const toastData = useAppSelector((state) => state.toast);
  const { t } = useTranslation();

  function initOperations() {
    const operations = featurePayload.reduce((acc: any, feature: any) => {
      const uniqueOperations = Array.from(
        new Map(featureListMap?.[feature.id]?.operations.map((op: any) => [op.id, op])).values()
      );

      const newInitialData = uniqueOperations.filter(
        (op: any) => !feature.actions.some((operation: any) => operation.id === op.id)
      );
      acc[feature.id] = {
        initialData: newInitialData,
        outputData: feature.actions,
        allActions: feature.allActions
      };
      return acc;
    }, {});
    return operations || {};
  }

  useEffect(() => {
    if (!params?.id) {
      setInitFlag(true);
      return;
    }

    if (!isEmpty(featureListMap) && !initFlag) {
      if (!isEmpty(featurePayload)) {
        setFeatureOperations(initOperations());
      }
      setInitFlag(true);
    }
  }, [featureListMap, featurePayload, initFlag]);

  const handleFeatureSelect = (feature: Array<any>) => {
    const [featureData] = feature;
    const { id: featureId } = featureData;
    setSelectedFeature(featureData);
    setSelectedFeatureId(featureId);
    if (!featureOperations[featureId]) {
      setFeatureOperations({
        ...featureOperations,
        [featureId]: {
          initialData: featureData?.operations || [],
          outputData: [],
          allActions: false
        }
      });
    }
    setRenderKey(Math.random());
  };

  const onRemove = (id: string) => {
    let clonedFeatureData = cloneDeep(featureOperations);
    delete clonedFeatureData?.[id];
    setFeatureOperations(clonedFeatureData);
    setSelectedFeature(null);
  };

  const updateFeaturePayload = (operations: any) => {
    const payload: any = Object.keys(operations).map((featureId) => {
      const { outputData, allActions } = operations[featureId];
      return {
        id: featureId,
        actions: outputData,
        allActions
      };
    });
    setFeaturePayload(payload);
  };

  const updateOperations = (featureId: any, initialData: any, outputData: any) => {
    let operations = {
      ...featureOperations,
      [featureId]: {
        initialData,
        outputData,
        allActions: initialData.length === 0 && outputData.length > 0
      }
    };
    setFeatureOperations(operations);
  };

  const {
    initialData = [],
    outputData = [],
    allActions = false
  } = featureOperations[selectedFeatureId] || {};

  const handleOperations = (input: any[], output: any[]) => {
    const inputOperations = Array.from(new Map(input.map((op) => [op.id, op])).values());
    const outputOperations = Array.from(new Map(output.map((op) => [op.id, op])).values());

    const newOutputData = outputOperations.filter(
      (op: any) => !inputOperations.some((operation: any) => operation.id === op.id)
    );

    const newInputData = inputOperations.filter(
      (op: any) => !outputOperations.some((operation: any) => operation.id === op.id)
    );

    updateOperations(
      selectedFeatureId,
      Array.from(new Map(newInputData.map((op: any) => [op.id, op])).values()),
      Array.from(new Map(newOutputData.map((op: any) => [op.id, op])).values())
    );
  };

  const setNewFeature = (data: any) => {
    setCreateFeatureModal(false);
    if (!isEmpty(data)) {
      handleFeatureSelect([data]);
      setFeatureListData([...featureListData, data]);
    }
  };

  const featureOperationsCallback = (featureOperations: any) => {
    const filteredOperations = Object.keys(featureOperations)
      .filter((key) => featureOperations[key].outputData.length > 0)
      .reduce((acc: any, key: string) => {
        acc[key] = featureOperations[key];
        return acc;
      }, {});
    updateFeaturePayload(filteredOperations);
  };

  useEffect(() => {
    featureOperationsRef.current = featureOperations;
  }, [featureOperations]);

  // useEffect(() => {
  //   return () => {
  //     featureOperationsCallback(featureOperationsRef.current);
  //   };
  // }, []);

  useEffect(() => {
    if (featureListData.length && initFlag) {
      featureOperationsCallback(featureOperations);
    }
    setRenderKey(Math.random());
  }, [featureOperations, initFlag]);

  useEffect(() => {
    const featureMap = featureListData.reduce((acc: any, obj: any) => {
      acc[obj.id] = obj;
      return acc;
    }, {});
    setFeatureListMap(featureMap);
    setFeatureListDataClone(featureListData);
  }, [featureListData]);

  const handleSearch = (text: string) => {
    if (!text) {
      setFeatureListDataClone(cloneDeep(featureListData));
      return;
    }

    const data = cloneDeep(featureListData);
    const keywords = text.toLowerCase().split(' ').filter(Boolean);

    const filteredData = data.filter((item: { name?: string; description?: string }) =>
      keywords.every((kw) => {
        const name = item.name?.toLowerCase() || '';
        const description = item.description?.toLowerCase() || '';
        return name.includes(kw) || description.includes(kw);
      })
    );

    setFeatureListDataClone(filteredData);
  };

  const toggleAll = () => {
    if (!selectedFeatureId) return;
    const combinedData = uniqBy([...initialData, ...outputData], 'id');
    const currentFlag = featureOperations?.[selectedFeatureId]?.allActions;

    updateOperations(
      selectedFeatureId,
      currentFlag ? combinedData : [],
      currentFlag ? [] : combinedData
    );
  };

  return (
    <div className="flex w-full flex-wrap p-4">
      {createFeatureModal && (
        <FeatureDetailModal
          openModal={createFeatureModal}
          setNewDataLoaded={setNewFeature}
          setOpenModal={setCreateFeatureModal}
        ></FeatureDetailModal>
      )}

      <div className="mb-10 w-full">
        <div className="w-2/5">
          <PickList
            fieldKey={'id'}
            key={featureListData.length}
            fieldValues={['name']}
            label={t('T_FEATURES_LIST')}
            addLabel={t('T_NEW_FEATURE')}
            selectedItems={selectedFeature ? [selectedFeature] : []}
            onSelectedChange={handleFeatureSelect}
            searchData={handleSearch}
            items={featureListDataClone}
            disabled={!isEdit}
            readOnly={false}
            searchable
            type={PICKLIST_TYPES.SINGLE}
            onAddItem={() => {
              setCreateFeatureModal(true);
            }}
            showAddOption={true} //!!checkForPermission([AppConstants.PERMISSION_FEATURE_CREATE])
          />
        </div>
        {featurePayload.length ? (
          <div className="mt-4 flex w-full flex-col">
            <div className="text-base font-normal">{t('T_FEATURES_SELECTED')}</div>
            <div className="flex w-full flex-row flex-wrap">
              {featurePayload.map((item: any, index: number) => {
                return (
                  <Pills
                    key={index}
                    name={featureListMap?.[item.id]?.name}
                    onClick={() => {
                      handleFeatureSelect([featureListMap?.[item.id]]);
                    }}
                    isSelected={selectedFeatureId === item.id}
                    onRemove={() => onRemove(item.id)}
                    editable={isEdit}
                  ></Pills>
                );
              })}
            </div>
          </div>
        ) : (
          ''
        )}
        {/* <div className="mt-5 flex w-full flex-row">
          <div className="text-base font-medium">{t('T_INCLUDE_ALL_OPERATIONS')}?</div>
          <div className="ml-4 flex flex-row">
            <Switch
              toggle={
                selectedFeatureId ? featureOperations?.[selectedFeatureId]?.allActions : false
              }
              onClick={toggleAll}
              data-testid="boolean-field"
              disabled={!isEdit}
              id="allOperations"
            >
              <ToggleButton
                toggle={
                  selectedFeatureId ? featureOperations?.[selectedFeatureId]?.allActions : false
                }
              />
            </Switch>
          </div>
        </div> */}
      </div>
      <DualingListPick
        key={renderKey}
        initialData={initialData}
        setInitialData={() => {}}
        handleOperation={handleOperations}
        outputData={outputData}
        setOutputData={() => {}}
        fieldKey="id"
        fieldValues={['actionName']}
        inputHeader={t('T_ROLE_OPERATIONS_AVAILABLE')}
        outputHeader={t('T_ROLE_OPERATIONS_SELECTED')}
        orderingControls={false}
        disabled={!isEdit}
        searchPlaceholder={t('T_SEARCH_OPERATION')}
        styles={{
          listWidth: 'w-[50%]',
          listHeight: 'h-[50rem]',
          buttonHeight: 'h-8',
          buttonWidth: 'w-8'
        }}
      />
    </div>
  );
};

export default AssignFeatures;
